import axios from 'axios';
import { getApiUrl } from '../config/api';

// Define uma baseURL única e consistente sempre com sufixo /api
const baseURL = getApiUrl();

const api = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Controle de logs via parâmetro/flag (sem precisar rebuild)
const shouldLogApi = () => (
  process.env.REACT_APP_API_DEBUG === 'true' ||
  (typeof localStorage !== 'undefined' && localStorage.getItem('DEBUG_API') === '1')
);

// Evitar instalar interceptors duplicados em HMR
if (!api.__loggingInterceptorsInstalled) {
  api.interceptors.request.use((config) => {
    if (shouldLogApi()) {
      const fullUrl = `${config.baseURL || ''}${config.url || ''}`;
      const hasAuth = Boolean(config.headers?.Authorization);
      console.log(`[API REQUEST] ${config.method?.toUpperCase()} ${fullUrl}`, {
        hasAuth,
        ts: new Date().toISOString()
      });
    }
    return config;
  });

  api.interceptors.response.use(
    (response) => {
      if (shouldLogApi()) {
        const fullUrl = `${response.config.baseURL || ''}${response.config.url || ''}`;
        console.log(`[API RESPONSE] ${response.status} ${fullUrl}`, {
          ts: new Date().toISOString()
        });
      }
      return response;
    },
    (error) => {
      if (shouldLogApi()) {
        const cfg = error.config || {};
        const fullUrl = `${cfg.baseURL || ''}${cfg.url || ''}`;
        console.warn(`[API ERROR] ${error.response?.status || 'ERR'} ${fullUrl}`, {
          message: error.response?.data?.message || error.message,
          ts: new Date().toISOString()
        });
      }
      return Promise.reject(error);
    }
  );

  api.__loggingInterceptorsInstalled = true;
}

// Helpers para alternar logs via Console do navegador
export const enableApiDebug = () => localStorage.setItem('DEBUG_API', '1');
export const disableApiDebug = () => localStorage.removeItem('DEBUG_API');

// Interceptor para adicionar o token em todas as requisições
api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Tratamento de 401 global no client (apenas uma vez)
if (!api.__responseInterceptorsInstalled) {
  api.interceptors.response.use(
    (response) => response,
    (error) => {
      if (shouldLogApi()) {
        const cfg = error.config || {};
        const fullUrl = `${cfg.baseURL || ''}${cfg.url || ''}`;
        console.warn('[API AUTH] 401 capturado', {
          url: fullUrl,
          method: cfg.method,
          ts: new Date().toISOString()
        });
      }
      if (error?.response?.status === 401) {
        try {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        } catch (_) {}
        if (typeof window !== 'undefined') {
          const isLoginPage = window.location.pathname === '/login' || window.location.pathname === '/';
          if (!isLoginPage) {
            window.location.href = '/login';
          }
        }
      }
      return Promise.reject(error);
    }
  );
  api.__responseInterceptorsInstalled = true;
}

// Serviços específicos para as novas funcionalidades
export const seguradoras = {
  // Listar todas as seguradoras
  getAll: () => api.get('/seguradoras'),
  
  // Criar nova seguradora
  create: (data) => api.post('/seguradoras', data),
  
  // Atualizar seguradora
  update: (id, data) => api.put(`/seguradoras/${id}`, data),
  
  // Excluir seguradora
  delete: (id) => api.delete(`/seguradoras/${id}`)
};

export const empresaSeguradora = {
  // Listar todos os mapeamentos
  getAll: () => api.get('/empresa-seguradora'),
  
  // Criar novo mapeamento
  create: (data) => api.post('/empresa-seguradora', data),
  
  // Atualizar mapeamento
  update: (id, data) => api.put(`/empresa-seguradora/${id}`, data),
  
  // Excluir mapeamento
  delete: (id) => api.delete(`/empresa-seguradora/${id}`)
};

export default api; 